import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import HelpIcon from '@mui/icons-material/Help';
import StorageIcon from '@mui/icons-material/Storage';
import QueueIcon from '@mui/icons-material/Queue';
import CloudIcon from '@mui/icons-material/Cloud';
import ApiIcon from '@mui/icons-material/Api';
import apiClient from '../../services/api';

interface ServiceStatus {
  status: string;
  [key: string]: any;
}

interface Container {
  name: string;
  service: string;
  port: number;
  status: string;
  health: string;
  uptime?: string;
}

interface QueueJob {
  id: number;
  queue: string;
  job_class: string;
  attempts: number;
  created_at: string;
  available_at: string;
}

interface FailedJob {
  id: number;
  uuid: string;
  connection: string;
  queue: string;
  exception: string;
  failed_at: string;
}

interface SystemOverview {
  timestamp: string;
  system: {
    php_version: string;
    laravel_version: string;
    environment: string;
    debug_mode: boolean;
  };
  services: {
    [key: string]: ServiceStatus;
  };
}

const SystemMonitor: React.FC = () => {
  const [overview, setOverview] = useState<SystemOverview | null>(null);
  const [containers, setContainers] = useState<Container[]>([]);
  const [queueData, setQueueData] = useState<any>(null);
  const [speckitStatus, setSpeckitStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = async () => {
    try {
      setError(null);
      
      const [overviewRes, dockerRes, queueRes, speckitRes] = await Promise.all([
        apiClient.get('/admin/monitor/overview'),
        apiClient.get('/admin/monitor/docker'),
        apiClient.get('/admin/monitor/queue'),
        apiClient.get('/admin/monitor/speckit'),
      ]);

      setOverview(overviewRes.data);
      setContainers(dockerRes.data.containers || []);
      setQueueData(queueRes.data);
      setSpeckitStatus(speckitRes.data);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch monitoring data');
      console.error('Monitoring error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Auto-refresh every 30 seconds
    const interval = autoRefresh ? setInterval(fetchData, 30000) : null;

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'running':
        return <CheckCircleIcon color="success" />;
      case 'degraded':
      case 'unhealthy':
        return <WarningIcon color="warning" />;
      case 'unknown':
      case 'assumed_running':
      case 'assumed_healthy':
        return <HelpIcon color="info" />;
      default:
        return <ErrorIcon color="error" />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'healthy':
      case 'running':
        return 'success';
      case 'degraded':
      case 'unhealthy':
        return 'warning';
      case 'unknown':
      case 'assumed_running':
      case 'assumed_healthy':
        return 'info';
      default:
        return 'error';
    }
  };

  if (loading && !overview) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          System Monitor
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Last refresh: {lastRefresh.toLocaleTimeString()}
          </Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* System Overview */}
      {overview && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              System Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  PHP Version
                </Typography>
                <Typography variant="h6">{overview.system.php_version}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Laravel Version
                </Typography>
                <Typography variant="h6">{overview.system.laravel_version}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Environment
                </Typography>
                <Chip 
                  label={overview.system.environment} 
                  color={overview.system.environment === 'production' ? 'error' : 'primary'}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Debug Mode
                </Typography>
                <Chip 
                  label={overview.system.debug_mode ? 'Enabled' : 'Disabled'} 
                  color={overview.system.debug_mode ? 'warning' : 'success'}
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Service Status Cards */}
      {overview && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {Object.entries(overview.services).map(([service, data]) => (
            <Grid item xs={12} sm={6} md={4} key={service}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {getStatusIcon(data.status)}
                    <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                      {service}
                    </Typography>
                  </Box>
                  <Chip 
                    label={data.status} 
                    color={getStatusColor(data.status)} 
                    size="small"
                  />
                  {data.driver && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Driver: {data.driver}
                    </Typography>
                  )}
                  {data.failed_jobs !== undefined && (
                    <Typography variant="body2" color={data.failed_jobs > 0 ? 'warning.main' : 'text.secondary'}>
                      Failed Jobs: {data.failed_jobs}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Docker Containers */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <StorageIcon />
            <Typography variant="h6">Docker Containers</Typography>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Container</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Port</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Health</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {containers.map((container) => (
                  <TableRow key={container.name}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {container.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{container.service}</TableCell>
                    <TableCell>{container.port}</TableCell>
                    <TableCell>
                      <Chip 
                        label={container.status} 
                        color={getStatusColor(container.status)} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getStatusIcon(container.health)}
                        <Typography variant="body2">{container.health}</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Queue Status */}
      {queueData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <QueueIcon />
              <Typography variant="h6">Queue Status</Typography>
            </Box>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Connection
                </Typography>
                <Typography variant="h6">{queueData.connection}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Pending Jobs
                </Typography>
                <Typography variant="h6">{queueData.statistics.pending}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Failed Jobs
                </Typography>
                <Typography variant="h6" color={queueData.statistics.failed > 0 ? 'warning.main' : 'inherit'}>
                  {queueData.statistics.failed}
                </Typography>
              </Grid>
            </Grid>

            {queueData.jobs && queueData.jobs.length > 0 && (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Pending Jobs
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Queue</TableCell>
                        <TableCell>Job Class</TableCell>
                        <TableCell>Attempts</TableCell>
                        <TableCell>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {queueData.jobs.map((job: QueueJob) => (
                        <TableRow key={job.id}>
                          <TableCell>{job.id}</TableCell>
                          <TableCell>{job.queue}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                              {job.job_class}
                            </Typography>
                          </TableCell>
                          <TableCell>{job.attempts}</TableCell>
                          <TableCell>{new Date(job.created_at).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {queueData.failed_jobs && queueData.failed_jobs.length > 0 && (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Recent Failed Jobs
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>UUID</TableCell>
                        <TableCell>Queue</TableCell>
                        <TableCell>Exception</TableCell>
                        <TableCell>Failed At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {queueData.failed_jobs.map((job: FailedJob) => (
                        <TableRow key={job.id}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                              {job.uuid.substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>{job.queue}</TableCell>
                          <TableCell>
                            <Tooltip title={job.exception}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                {job.exception}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{new Date(job.failed_at).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {queueData.info && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {queueData.info}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Speckit Status */}
      {speckitStatus && (
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <ApiIcon />
              <Typography variant="h6">Speckit Status</Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Service
                </Typography>
                <Typography variant="h6">{speckitStatus.service}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  {getStatusIcon(speckitStatus.status)}
                  <Chip 
                    label={speckitStatus.status} 
                    color={getStatusColor(speckitStatus.status)} 
                    size="small"
                  />
                </Box>
              </Grid>
              {speckitStatus.url && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    URL
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {speckitStatus.url}
                  </Typography>
                </Grid>
              )}
              {speckitStatus.version && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Version
                  </Typography>
                  <Typography variant="body2">{speckitStatus.version}</Typography>
                </Grid>
              )}
              {speckitStatus.error && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    {speckitStatus.error}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SystemMonitor;
